// src/services/likes.ts
let fetchFn: typeof fetch | undefined;
let fs: typeof import("fs") | undefined;
let path: typeof import("path") | undefined;

// Robust node-vs-browser detection (works under SSR)
const isNode =
  typeof window === "undefined" &&
  typeof process !== "undefined" &&
  !!(process.versions && process.versions.node);

// In browsers, bind fetch once. In Node we intentionally leave it undefined.
if (!isNode && typeof window !== "undefined" && typeof window.fetch === "function") {
  fetchFn = window.fetch.bind(window);
}

// Lazy-load Node builtins only when actually needed, using eval('require') so bundlers don't rewrite it.
function ensureNodeDeps(): void {
  if (!isNode || (fs && path)) return;
  try {
    const req = Function("return typeof require==='function' ? require : undefined")() as undefined | ((m: string) => any); // avoid static require for esbuild/browser
    if (!req) return;
    // Try node: specifiers first (Node 16+), then fall back
    fs = (req("node:fs") as typeof import("fs") | undefined) || (req("fs") as typeof import("fs") | undefined);
    path = (req("node:path") as typeof import("path") | undefined) || (req("path") as typeof import("path") | undefined);
  } catch {
    // ignore — Node fallback just won't work if not available
  }
}

export interface LikesBackend {
  getCount(id: string): Promise<number | undefined>;
  setLiked(id: string, liked: boolean): Promise<number | undefined>;
}

function readMeta(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  return el?.content?.trim() || undefined;
}

function resolveLikesApi(): string {
  if (typeof window !== "undefined") {
    const injected = (window as any).__STORYCORE_LIKES_API__;
    if (injected) return injected;
  }

  const baseMeta = readMeta("storycore-base");
  const fallbackBase =
    baseMeta ||
    (typeof window !== "undefined" ? window.location.pathname.replace(/\/[^\/]*$/, "") || "/" : "/");
  const cleaned = fallbackBase.replace(/\/$/, "");
  return `${cleaned}/api/likes`;
}

function credentialsFor(api: string): RequestCredentials {
  try {
    const apiUrl = new URL(api, typeof window !== "undefined" ? window.location.href : undefined);
    const currentOrigin = typeof window !== "undefined" ? window.location.origin : "";
    return apiUrl.origin === currentOrigin ? "same-origin" : "omit";
  } catch {
    return "same-origin";
  }
}

function parseCount(data: any): number | undefined {
  const v = data?.count ?? data?.likes;
  if (typeof v === "number") return Number.isFinite(v) ? v : undefined;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

async function fetchWithTimeout(input: RequestInfo, init: RequestInit = {}, ms = 3500) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  try {
    return await fetchFn!(input, { ...init, signal: ctrl.signal });
  } finally {
    clearTimeout(t);
  }
}

// --- per-video JSON storage (Node fallback) ---
function safeIdToFilename(id: string): string {
  // Allow letters, numbers, dash, underscore; replace others with underscore
  const cleaned = id.trim().toLowerCase().replace(/[^a-z0-9._-]+/g, "_").replace(/^_+|_+$/g, "");
  return cleaned || "unnamed";
}

function localLikesDir(): string | undefined {
  ensureNodeDeps();
  if (!path) return undefined;
  // Store files under ../data/likes relative to compiled JS
  return path.resolve(__dirname, "../data/likes");
}

async function readLocalLikesFor(id: string): Promise<number | undefined> {
  ensureNodeDeps();
  if (!fs || !path) return undefined;
  const dir = localLikesDir();
  if (!dir) return undefined;
  const filePath = path.join(dir, safeIdToFilename(id) + ".json");
  try {
    const content = await fs.promises.readFile(filePath, "utf-8");
    const obj = JSON.parse(content);
    const v = obj?.count ?? obj?.likes;
    return typeof v === "number" ? v : undefined;
  } catch {
    // If not found, treat as undefined
    return undefined;
  }
}

async function writeLocalLikesFor(id: string, count: number): Promise<void> {
  ensureNodeDeps();
  if (!fs || !path) return;
  const dir = localLikesDir();
  if (!dir) return;
  try {
    await fs.promises.mkdir(dir, { recursive: true });
    const filePath = path.join(dir, safeIdToFilename(id) + ".json");
    const payload = { count };
    await fs.promises.writeFile(filePath, JSON.stringify(payload, null, 2), "utf-8");
  } catch {
    // noop
  }
}

// --- availability probe + timeout ---
// We memoize SUCCESS permanently (no point re-probing a known-good endpoint).
// We do NOT memoize failure — slow networks can falsely fail the 2s probe,
// permanently disabling likes for the rest of the session. Instead, callers
// fall back to attempting the real GET directly when the probe fails;
// per-call timeouts on the real GET catch genuinely-broken endpoints
// without sticking the whole service into a dead state.
let _apiAvailable: boolean | undefined;
let _probePromise: Promise<boolean> | undefined;

async function probeApi(): Promise<boolean> {
  if (_apiAvailable === true) return true;        // cached success
  if (_probePromise) return _probePromise;         // collapse in-flight probes

  _probePromise = (async () => {
    const API = resolveLikesApi();
    try {
      if (typeof fetchFn === "undefined") return false;
      const query = `${API}?video=__probe__&slug=__probe__&probe=1`;
      const res = await fetchWithTimeout(
        query,
        { credentials: credentialsFor(API), cache: "no-store" },
        6000  // was 2000 — 3G connections need more time
      );
      if (res.ok) {
        _apiAvailable = true;   // memoize success
        return true;
      }
      return false;             // don't memoize — let next call retry
    } catch {
      return false;             // don't memoize — let next call retry
    } finally {
      _probePromise = undefined;
    }
  })();

  return _probePromise;
}

// --- simple in-flight GET cache to avoid duplicate calls per id ---
const inflightGet = new Map<string, Promise<number | undefined>>();

export const LikesService: LikesBackend = {
  async getCount(id: string) {
    if (!id) return undefined;

    // collapse concurrent identical GETs
    const existing = inflightGet.get(id);
    if (existing) return existing;

    const task = (async () => {
      // Try probe first (fast path when API is known-good).
      // If probe fails (e.g. slow connection caused a timeout), still attempt
      // the real GET — its own longer timeout will catch a truly broken
      // endpoint, but a slow-but-working one gets a chance to respond.
      const probeOk = await probeApi();
      if (probeOk || typeof fetchFn !== "undefined") {
        const API = resolveLikesApi();
        const query = `${API}?video=${encodeURIComponent(id)}&slug=${encodeURIComponent(id)}`;
        try {
          const r = await fetchWithTimeout(
            query,
            { credentials: credentialsFor(API), cache: "no-store" },
            8000  // was 3500 — same reason as probe bump: 3G needs more time
          );
          if (!r.ok) return undefined;
          // Successful real call retroactively confirms availability
          _apiAvailable = true;
          return parseCount(await r.json());
        } catch {
          return undefined;
        }
      } else if (isNode) {
        try {
          ensureNodeDeps();
          if (fs && path) {
            return await readLocalLikesFor(id);
          }
          return undefined;
        } catch {
          return undefined;
        }
      } else {
        return undefined;
      }
    })();

    inflightGet.set(id, task);
    const out = await task.finally(() => inflightGet.delete(id));
    return out;
  },

  async setLiked(id: string, liked: boolean) {
    if (!id) return undefined;
    // Same fallback pattern as getCount — if probe fails, still try the real
    // POST. POSTs on slow connections deserve the same patience.
    const probeOk = await probeApi();
    if (probeOk || typeof fetchFn !== "undefined") {
      const API = resolveLikesApi();
      try {
        const url = `${API}?video=${encodeURIComponent(id)}&slug=${encodeURIComponent(id)}`;
        const body = JSON.stringify({ video: id, slug: id, liked });
        const r = await fetchWithTimeout(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: credentialsFor(API),
          cache: "no-store",
          body
        }, 8000);  // was 4000 — be patient on slow links
        if (!r.ok) return undefined;
        _apiAvailable = true;  // retroactively confirm
        return parseCount(await r.json());
      } catch {
        return undefined;
      }
    } else if (isNode) {
      try {
        ensureNodeDeps();
        if (fs && path) {
          const current = (await readLocalLikesFor(id)) ?? 0;
          const next = liked ? current + 1 : Math.max(0, current - 1);
          await writeLocalLikesFor(id, next);
          return next;
        }
        return undefined;
      } catch {
        return undefined;
      }
    } else {
      return undefined;
    }
  }
};

/** Extracts the last path segment (including extension) from a URL-like string. */
function extractFilename(urlish: string): string {
  try {
    // Handle absolute and relative URLs
    const u = new URL(urlish, typeof window !== "undefined" ? window.location.href : "http://local/");
    const path = u.pathname || "";
    const last = path.split("/").filter(Boolean).pop() || "";
    return last || urlish;
  } catch {
    // Fallback: manual parse (handles blob: and odd strings)
    const stripped = urlish.split("?")[0].split("#")[0];
    const parts = stripped.split("/");
    return parts[parts.length - 1] || stripped;
  }
}

/**
 * Builds a like ID tied to a specific <video> element's source filename.
 * Default returns just the filename (e.g., "reel-01.mp4").
 * Pass opts.scope = "page+filename" to namespace by page slug as well.
 */
export function likeIdForVideo(
  videoEl: HTMLVideoElement,
  opts?: { scope?: "filename" | "page+filename" | "domain+filename" }
): string {
  const scope = opts?.scope || "filename";
  // Prefer currentSrc (post-source selection), fallback to @src or the first <source>
  const src =
    (videoEl.currentSrc && videoEl.currentSrc !== (typeof window !== "undefined" ? window.location.href : "")) ? videoEl.currentSrc :
    (videoEl.getAttribute("src") || videoEl.querySelector("source")?.getAttribute("src") || "");

  const filename = extractFilename(src || "unknown.mp4");

  if (scope === "page+filename") {
    const pageSlug =
      readMeta("storycore-slug") ||
      (typeof window !== "undefined"
        ? window.location.pathname.replace(/[^A-Za-z0-9._/~-]+/g, "_")
        : "page");
    return `${pageSlug}#${filename}`;
  }
  if (scope === "domain+filename") {
    const host =
      (typeof window !== "undefined" && window.location.hostname) ||
      readMeta("storycore-domain") ||
      "unknown";
    return `${host}#${filename}`;
  }
  return filename;
}

// localStorage helpers (single source of truth for Like state)
export function readLiked(id: string): boolean {
  try { return localStorage.getItem(`storycore-like-${id}`) === "1"; } catch { return false; }
}
export function writeLiked(id: string, liked: boolean): void {
  try { localStorage.setItem(`storycore-like-${id}`, liked ? "1" : "0"); } catch {}
}

// Convenience wrapper for buttons.ts or other UI modules
export async function sendLikeUpdate(id: string, liked: boolean): Promise<{ count?: number; liked: boolean }> {
  const count = await LikesService.setLiked(id, liked);
  if (count !== undefined) {
    writeLiked(id, liked);
  }
  return { count, liked: readLiked(id) };
}
